import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { useForm } from "react-hook-form";
import { Form } from "semantic-ui-react";
import { Helmet } from "react-helmet-async";

import { getToken, isBrowser } from "../../../services/auth";

import TextArea from "../../../components/textArea/TextArea";
import Select from "../../../components/select/Select";
import Button from "../../../components/button/Button";
import Modal from "../../../components/modal/Modal";
import Loader from "../../../components/modal/Loader";
import MarkdownEditor from "../../../components/markdownEditor/MarkdownEditor";
import { config } from '../../../config/env';

const AddOverview = () => {
  // DEFAULT STATE
  const defaultState = {
    isLoading: false,
    showModal: false,
    modalTitle: "",
    modalSubText: "",
    modalIcon: "",
    confirmButtonText: "",
    confirmButtonAction: null,
    previewMarkdown: false
  };

  const defaultStatusList = [
    { id: 0, name: "Inactive" },
    { id: 1, name: "Active" }
  ];

  // HOOKS
  const [state, setState] = useState(defaultState);
  const [statusList, setStatusList] = useState([]);
  const [images, setImages] = useState([])
  const navigate = useNavigate();
  const {
    register,
    handleSubmit,
    getValues,
    setValue,
    formState: { errors },
  } = useForm();

  // EVENT HANDLERS
  const onSubmit = async (e) => {
    const newOverview = {
      description: e.description,
      is_active: e.status_id === 1,
      image_filenames: []
    };
    if(e.content){
      newOverview.content = e.content
    }
    if(e.image_filenames && e.image_filenames.length > 0) {
      newOverview.image_filenames = e.image_filenames
    }

    if (isBrowser()) {
      setState(prev => ({...prev, isLoading: true}))
      fetch(`${config.apiBaseUrl}/api/v1/admin/overview/`, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
          Authorization: `Bearer ${await getToken()}`,
        },
        body: JSON.stringify(newOverview),
      }).then(async(res) => {
        if (res.status === 200) {
          setState(prev => ({...prev, 
            modalIcon: "success",
            modalTitle: "Successful",
            modalSubText: "Overview was added successfully",
            confirmButtonText: "Go to overviews list",
            confirmButtonAction: handleConfirmButtonAction
          }));
        } else {
          let errorMessage = "Something went wrong.";
          try {
            const errorData = await res.json();
            errorMessage = errorData?.message || JSON.stringify(errorData);
          } catch (jsonErr) {
            try {
              errorMessage = await res.text();
            } catch (textErr) {
              errorMessage = "Unable to read error message.";
            }
          }
          setState(prev => ({...prev, 
            modalIcon: "error",
            modalTitle: "Error",
            modalSubText: errorMessage,
            confirmButtonText: "Close",
            confirmButtonAction: handleResetModal
          }));
        }
      }).catch((error) => {
        setState(prev => ({...prev, 
          modalIcon: "error",
          modalTitle: "Error",
          modalSubText: error.toString(),
          confirmButtonText: "Close",
          confirmButtonAction: handleResetModal
        }));
      }).finally(() => {
        setState(prev => ({...prev, 
          isLoading: false,
          showModal: true
        }));
      });
    }
  };

  const onCancel = () => {
    navigate("/ava-admin/overview");
  };

  const handleConfirmButtonAction = () => {
    navigate("/ava-admin/overview");
  };

  const handleResetModal = () => {
    setState(prev => ({...prev, 
      showModal: false,
      confirmButtonAction: null
    }));
  };

  const handleStatusChange = (item) => {
    setValue("status_id", item.id)
  }

  useEffect(() => {
    setStatusList(defaultStatusList);
    register("status_id", { required: true });
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // JSX
  const renderStatusSelect = () => {
    if (!statusList || statusList.length === 0) {
      console.log("No status list available");
      return null;
    }
    console.log("Rendering status select with list:", statusList);
    return (
      <Select 
      items={statusList}
      label="Status"
      itemChangeHandler={handleStatusChange}
      error={errors.status_id} />
    )
  };

  return (
    <>
      <Helmet>
        <title>Avanade Research - Admin</title>
      </Helmet>
      <div page="overviews">
        <div className="w-[calc(100%-40px)] min-h-[calc(100%-40px)] m-[20px] bg-white rounded-2xl p-10 shadow-lg text-sm">
          <div className="max-w-[1280px] mx-auto">
            <div className="flex w-full">
              <div className="block w-full">
                <div className="text-lg font-semibold">Add Overview</div>
                <div className="text-gray-600">
                  
                </div>
              </div>
            </div>

            <div className="mt-10">
              <Form>
                <Form.Field>
                  <div className="mt-5">
                    <TextArea
                      label="Description"
                      id="description"
                      script={register("description", { required: false })}
                      rows="4"
                    />
                  </div>
                  <MarkdownEditor 
                    images={images} setImages={setImages}
                    isLoading={state.isLoading} setIsLoading={(loading) => setState(prev => ({...prev, isLoading: loading}))}
                    getValues={getValues} setValue={setValue}
                    previewMarkdown={state.previewMarkdown} setPreviewMarkdown={(previewMarkdown) => setState(prev => ({...prev, previewMarkdown: previewMarkdown}))}
                    register={register} errors={errors}
                  />
                  <div className="mt-5">
                    { renderStatusSelect() }
                    <small className="text-gray-500 text-xs block mt-1">
                      Setting as active will deactivate the current active overview.
                    </small>
                  </div>
                </Form.Field>
                <div className="flex justify-end mt-7">
                  <Button type="button" label="Cancel" color="gray-200" text="gray-800" onClick={onCancel} />
                  <Button type="submit" label="Submit" className="ml-4" onClick={handleSubmit(onSubmit)} />
                </div>
              </Form>

              {state.isLoading && <Loader />}

              {state.showModal && (
                <Modal
                  title={state.modalTitle}
                  subtext={state.modalSubText}
                  confirmButtonText={state.confirmButtonText}
                  confirmButtonAction={state.confirmButtonAction}
                  icon={state.modalIcon}
                  onCancel={handleResetModal}
                />
              )}
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default AddOverview;
